import LayersIcon from '@mui/icons-material/Layers';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { IBestPage } from 'common/interfaces/agg-page';
import { IFetchBestPagesOptions } from 'common/interfaces/pages-api';
import { getProductsApiClient } from 'common/services/products-api.service';
import { ErrorMessage } from 'common/ui/error-message/ErrorMessage';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DownloadIcon, PlusOutlinedIcon } from '../../../../assets/images/icons';
import { FormMode, Marketplaces } from '../../../../common/constants/enums';
import { DraftOptionType } from '../../../../common/constants/select-options';
import { getAggPageApiClient } from '../../../../common/services/agg-pages-api.service';
import { Button } from '../../../../common/ui/button';
import { Select } from '../../../../common/ui/select';
import { utilDownloadCsv, utilPagePathGenerator } from '../../../../common/utils';
import { useActions, useAppSelector, useTableFilter } from '../../../../hooks';
import { pagesApiSlice } from '../../../../store/api/pages-api/pages-api';
import { GetAggPagesV2Params } from '../../../../store/api/pages-api/pages-api-types';
import { PageTitle } from '../../../common/partials/PageTitle/PageTitle';
import { PagesFilter, PagesTable, PageTypeOption } from '../../components';
import { AggPagesV2Table } from '../../components/agg-pages-v2-table/agg-pages-v2-table';
import { v2Theme } from '../page-builder-v2/v2-theme';
import styles from './pages.module.scss';

export type PagesFilterOptions = Omit<IFetchBestPagesOptions, 'attribution'> & {
  pageType?: PageTypeOption;
  slug?: string;
};

export const pagesFilterOptions: PagesFilterOptions = {
  author: null,
  brand: null,
  domain: '',
  draft: null,
  keyword: '',
  limit: 25,
  marketplace: Marketplaces.Amazon,
  no_cache: 1,
  offset: 0,
  pageType: 'single',
  publisher: null,
  status: null,
};

interface SelectedPageData {
  path: string;
  id: number;
}

export const Pages = () => {
  const { pageStatusesList, pagesList } = useAppSelector(({ pages }) => pages);
  const { fetchPageStatusesListRequest, fetchPagesListRequest } = useActions();
  const [pageStatusOptions, setPageStatusOptions] = useState<DraftOptionType[]>([]);
  const [selectedPages, setSelectedPages] = useState<SelectedPageData[]>([]);
  const [csvLoader, setCsvLoader] = useState(false);
  const [pagesFilter, setPagesFilter] = useTableFilter<PagesFilterOptions>(pagesFilterOptions);

  const navigate = useNavigate();
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const isMultiple = pagesFilter.pageType === 'multiple';

  const [updateAggPageV2] = pagesApiSlice.useUpdateAggPageV2Mutation();
  const [downloadAggPagesV2, { isLoading: downloadV2Loading }] = pagesApiSlice.useDownloadAggPagesV2Mutation();

  const aggPagesParams: GetAggPagesV2Params = {
    author_id: pagesFilter.author || undefined,
    limit: pagesFilter.limit,
    offset: pagesFilter.offset,
    publisher_id: typeof pagesFilter.publisher === 'number' ? pagesFilter.publisher : undefined,
    slug: pagesFilter.slug || undefined,
    status: pagesFilter.status || undefined,
  };

  const { data: aggPagesData, isFetching: aggPagesLoading } = pagesApiSlice.useGetAggPagesV2Query(
    aggPagesParams,
    { skip: !isMultiple },
  );

  const selectedPageHandler = (e: ChangeEvent<HTMLInputElement>, path: string) => {
    const currentId = Number(e.target.id);
    if (selectedPages.find((elem) => elem.id === currentId)) {
      setSelectedPages((prev) => [...prev.filter(({ id }) => id !== currentId)]);
    } else {
      setSelectedPages((prevState) => [...prevState, { id: currentId, path }]);
    }
  };

  const downloadCsvFile = async (
    keyword: string,
    filters: Partial<IFetchBestPagesOptions> = {},
  ) => {
    if (isMultiple) {
      await downloadAggPagesV2(aggPagesParams);
      return;
    }
    setCsvLoader(true);
    try {
      const res = await getProductsApiClient().downloadCsvFile(keyword, filters);
      if (res.data) {
        const fileName = res.headers['content-disposition'].match(/filename="(.*)"/)[1];
        utilDownloadCsv(res.data, fileName);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCsvLoader(false);
    }
  };

  const updatePageStatus = async (id: number, data: Partial<Pick<IBestPage, 'status'>>) => {
    try {
      await getAggPageApiClient().updatePage(id, data);
    } catch (e) {
      console.error(e);
    } finally {
      fetchPagesListRequest({ filters: pagesFilter });
    }
  };

  const multiplePageStatusChange = async (pageIds: number[], status: string) => {
    try {
      const promises = pageIds.map((id) => getAggPageApiClient().updatePage(id, { status }));
      await Promise.all(promises);
      setSelectedPages([]);
      fetchPagesListRequest({ filters: pagesFilter });
    } catch (e) {
      console.error(e);
    }
  };

  const allPagesSelect = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    const pagesId = pagesList.pages.map((page) => ({
      id: page.page_id,
      path: utilPagePathGenerator(
        {
          brand: page?.brand,
          params: {
            mode: FormMode.DUPLICATE
          },
          publisher: page?.publisher,
          slug: page?.slug
        },
        {},
      ),
    }));

    if (checked && pagesList.pages.length !== selectedPages.length) {
      setSelectedPages(pagesId);
    }

    if (!checked && pagesList.pages.length === selectedPages.length) {
      setSelectedPages([]);
    }
  };

  const openPagesDuplicate = () => {
    selectedPages.forEach(({ path }) => window.open(`/pages/${path}`, '_blank'));
    setSelectedPages([]);
  };

  useEffect(() => {
    fetchPageStatusesListRequest();
  }, []);

  useEffect(() => {
    if (!isMultiple) {
      fetchPagesListRequest({ filters: { ...pagesFilter, layout: null } });
    }
  }, [pagesFilter, isMultiple]);

  useEffect(() => {
    const pagesStatusOptions = pageStatusesList.pageStatuses.map((status) => ({
      label: status,
      value: status,
    }));
    setPageStatusOptions([...pagesStatusOptions]);
  }, [pageStatusesList.pageStatuses]);

  return (
    <div className={styles.pages}>
      <div className={styles.page_top}>
        <PageTitle description="Let's check you pages today" title="Pages" />
        <div className="d-flex w-100 justify-end column-gap-20">
          <Button iconStart={<PlusOutlinedIcon />} onClick={() => setAddDialogOpen(true)}>
            Add New
          </Button>
        </div>

        <ThemeProvider theme={v2Theme}>
        <Dialog fullWidth maxWidth="xs" onClose={() => setAddDialogOpen(false)} open={addDialogOpen}>
          <DialogTitle sx={{ pb: 1 }}>
            <Typography fontWeight={600} variant="subtitle1">Create New Page</Typography>
            <Typography color="text.secondary" mt={0.5} variant="body2">Choose the page type to get started</Typography>
          </DialogTitle>
          <DialogContent sx={{ pb: 3 }}>
            <Stack spacing={1.5}>
              <MuiButton
                fullWidth
                onClick={() => { setAddDialogOpen(false); navigate('/pages/new'); }}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 1.5,
                  px: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  color: 'text.primary',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: 'primary.50' },
                }}
                variant="outlined"
              >
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, width: '100%' }}>
                  <Box sx={{
                    alignItems: 'center',
                    backgroundColor: 'action.hover',
                    borderRadius: 1.5,
                    display: 'flex',
                    flexShrink: 0,
                    height: 40,
                    justifyContent: 'center',
                    width: 40,
                  }}>
                    <ViewDayIcon color="action" />
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography fontWeight={600} variant="body2">Single Marketplace</Typography>
                    <Typography color="text.secondary" variant="caption">
                      All products from the same marketplace (v1)
                    </Typography>
                  </Box>
                </Box>
              </MuiButton>
              <MuiButton
                fullWidth
                onClick={() => { setAddDialogOpen(false); navigate('/pages/v2/new'); }}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 1.5,
                  px: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  color: 'text.primary',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: 'primary.50' },
                }}
                variant="outlined"
              >
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, width: '100%' }}>
                  <Box sx={{
                    alignItems: 'center',
                    backgroundColor: 'primary.50',
                    borderRadius: 1.5,
                    display: 'flex',
                    flexShrink: 0,
                    height: 40,
                    justifyContent: 'center',
                    width: 40,
                  }}>
                    <LayersIcon color="primary" />
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography fontWeight={600} variant="body2">Multiple Marketplace</Typography>
                    <Typography color="text.secondary" variant="caption">
                      Products from different marketplaces (v2)
                    </Typography>
                  </Box>
                </Box>
              </MuiButton>
            </Stack>
          </DialogContent>
        </Dialog>
        </ThemeProvider>
      </div>

      <div className="mt-40 mb-20 w-100 d-flex column-gap-10 justify-space-between">
        {selectedPages.length > 0 ? (
          <div className="d-flex column-gap-10 w-30">
            <Select
              className="w-100"
              onChange={(option) =>
                multiplePageStatusChange(
                  selectedPages.map((elem) => elem.id),
                  option.value,
                )
              }
              options={pageStatusOptions}
              placeholder={`Change status ( ${selectedPages.length} )`}
            />
            <Button fullWidth onClick={openPagesDuplicate} outlined variant="secondary">
              {`Duplicate ( ${selectedPages.length} )`}
            </Button>
          </div>
        ) : (
          <PagesFilter
            filter={pagesFilter}
            statusOptions={pageStatusOptions}
            updateFilter={setPagesFilter}
          />
        )}
        <Button
          iconStart={<DownloadIcon />}
          loading={csvLoader || downloadV2Loading}
          onClick={() => downloadCsvFile(pagesFilter.keyword, pagesFilter)}
          variant="secondary"
        />
      </div>

      {isMultiple ? (
        <AggPagesV2Table
          loading={aggPagesLoading}
          onDeleted={() => {}}
          onStatusChange={(id, status) => updateAggPageV2({ pageId: id, body: { status } })}
          pages={aggPagesData?.pages ?? []}
          pagination={{
            count: aggPagesData?.count ?? 0,
            limit: pagesFilter.limit,
            onPagination: (params) => setPagesFilter(params as Partial<PagesFilterOptions>),
          }}
          statusOptions={pageStatusOptions}
        />
      ) : (
        <>
          {!pagesList.error && (
            <PagesTable
              allPagesSelect={allPagesSelect}
              filters={pagesFilter}
              loading={pagesList.loading}
              pages={pagesList.pages}
              pagination={{
                count: pagesList.totalCount,
                limit: pagesFilter.limit,
                onPagination: setPagesFilter,
              }}
              selectedPageHandler={selectedPageHandler}
              selectedPages={selectedPages}
              statusOptions={pageStatusOptions}
              updatePageStatus={updatePageStatus}
            />
          )}
          {pagesList.error && <ErrorMessage error={pagesList.error.message} />}
        </>
      )}
    </div>
  );
};
